import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type {
  ConversationFile,
  LaunchDocumentChoices,
  LaunchFormFile,
  LaunchFormSpec,
  LaunchFormTemplateDocument,
} from '@aline/common/chat/launch-form';
import {
  addLaunchExtraDocument,
  applyLaunchDocumentChoices,
  excludeLaunchTemplateDocument,
  getAddableLaunchFiles,
  getConversationFileSourcePhrase,
  getLaunchDocumentBlockId,
  getReplacementLine,
  getSkippedFileLine,
  LAUNCH_EXTRA_DOCUMENTS_BLOCK_ID,
  LAUNCH_FORM_COPY,
  removeLaunchExtraDocument,
  replaceLaunchTemplateDocument,
  restoreLaunchTemplateDocument,
} from '@aline/common/chat/launch-form';
import { GQL } from '@aline/graphql';
import {
  Button,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import { ChevronDown, FileText, Plus, Undo2, Upload, X } from 'lucide-react';
import { AlineTooltip } from 'src/components/generic/AlineTooltip';
import { makeStyles } from 'tss-react/mui';

const ICON_SIZE = 16;

const DOCUMENT_ROLE_LABELS: Record<GQL.WorkflowFileDocumentRole, string> = {
  [GQL.WorkflowFileDocumentRole.General]: 'General',
  [GQL.WorkflowFileDocumentRole.Signing]: 'For signature',
};

type UploadedFile = Pick<LaunchFormFile, 'documentId' | 'name'>;

/** Where an upload lands: third-party paper for one template document, or an extra document. */
interface LaunchDocumentUploadTarget {
  templateDocumentId: string | null;
}

export interface LaunchDocumentUploadSlotProps {
  onCancel: () => void;
  onUploaded: (file: UploadedFile) => void;
  target: LaunchDocumentUploadTarget;
}

interface LaunchDocumentsSectionProps {
  choices: LaunchDocumentChoices;
  onChange: (choices: LaunchDocumentChoices) => void;
  /** Each surface uploads its own way: the hosted page through its token API, the app through Filestack. */
  renderUpload: (props: LaunchDocumentUploadSlotProps) => React.ReactNode;
  spec: LaunchFormSpec;
  disabled?: boolean;
  /** Keyed by `getLaunchDocumentBlockId` and `LAUNCH_EXTRA_DOCUMENTS_BLOCK_ID`, as the server returns them. */
  errors?: Record<string, string>;
}

interface MenuTriggerProps {
  isOpen: boolean;
  onClick: (event: React.MouseEvent<HTMLElement>) => void;
}

const useStyles = makeStyles()(theme => ({
  actions: {
    alignItems: 'center',
    display: 'flex',
    flexShrink: 0,
    gap: theme.spacing(0.5),
    justifyContent: 'flex-end',
  },
  caption: {
    color: theme.appColors.secondaryType,
    fontSize: 12,
  },
  error: {
    color: theme.palette.error.main,
    fontSize: 12,
  },
  excluded: {
    opacity: 0.5,
  },
  header: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
  },
  icon: {
    color: theme.appColors.iconsType,
    flexShrink: 0,
  },
  menuIcon: {
    // Matches the specificity of the theme's MenuItem icon override so this min width wins.
    '&.MuiListItemIcon-root': {
      color: theme.appColors.iconsType,
      minWidth: 28,
    },
  },
  name: {
    fontSize: 14,
    fontWeight: 600,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  replaceButton: {
    '& .MuiButton-endIcon': {
      marginLeft: theme.spacing(0.25),
    },
    color: theme.appColors.secondaryType,
    fontSize: 14,
    minWidth: 0,
    padding: theme.spacing(0.25, 1),
    textTransform: 'none',
  },
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  row: {
    alignItems: 'center',
    border: `1px solid ${theme.appColors.rule}`,
    borderRadius: 8,
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    justifyContent: 'space-between',
    padding: theme.spacing(1, 1.5),
  },
  rowBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
  },
  rowText: {
    alignItems: 'center',
    display: 'flex',
    flex: '1 1 200px',
    gap: theme.spacing(1),
    minWidth: 0,
  },
  rowTextColumn: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
  },
  selected: {
    '&, &:hover': {
      backgroundColor: theme.appColors.selectedBackground,
      color: theme.appColors.primary,
    },
  },
  title: {
    fontSize: 16,
    fontWeight: 600,
  },
}));

/**
 * The Documents section of a launch form, shared by the hosted page and the in-app approval
 * card. Each template document can be kept, replaced with third-party paper (a file from the
 * conversation or a fresh upload) or left out; extra documents can be removed or added.
 */
export const LaunchDocumentsSection = React.memo(function LaunchDocumentsSection({
  choices,
  disabled = false,
  errors = {},
  onChange,
  renderUpload,
  spec,
}: LaunchDocumentsSectionProps) {
  const { classes } = useStyles();
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [uploadTarget, setUploadTarget] = useState<LaunchDocumentUploadTarget | null>(null);
  const display = useMemo(
    () => applyLaunchDocumentChoices({ choices, spec, uploadedFiles }),
    [choices, spec, uploadedFiles]
  );
  const replaceableFiles = useMemo(
    () => spec.conversationFiles.filter(file => file.canReplace),
    [spec.conversationFiles]
  );
  const addableFiles = useMemo(() => getAddableLaunchFiles(spec, choices), [choices, spec]);

  /**
   * An upload resolves after the user may have cancelled it, opened another slot or changed other
   * rows, so it applies only to the slot that is still open and on top of the latest choices.
   */
  const uploadTargetRef = useRef<LaunchDocumentUploadTarget | null>(null);
  const choicesRef = useRef(choices);
  useEffect(() => {
    choicesRef.current = choices;
  }, [choices]);

  const showUpload = useCallback((target: LaunchDocumentUploadTarget | null) => {
    uploadTargetRef.current = target;
    setUploadTarget(target);
  }, []);
  const cancelUpload = useCallback(() => showUpload(null), [showUpload]);
  const renderUploadSlot = (target: LaunchDocumentUploadTarget) =>
    renderUpload({
      onCancel: cancelUpload,
      onUploaded: file => {
        if (uploadTargetRef.current !== target) return;
        setUploadedFiles(current => [...current, file]);
        const { templateDocumentId } = target;
        onChange(
          templateDocumentId
            ? replaceLaunchTemplateDocument(choicesRef.current, {
                documentId: file.documentId,
                templateDocumentId,
              })
            : addLaunchExtraDocument(choicesRef.current, file.documentId)
        );
        cancelUpload();
      },
      target,
    });

  if (!spec.templateDocuments.length && !spec.conversationFiles.length && !spec.skippedFiles.length)
    return null;

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <Typography className={classes.title}>{LAUNCH_FORM_COPY.documentsTitle}</Typography>
        <FilePickerMenu
          files={addableFiles}
          renderTrigger={({ isOpen, onClick }) => (
            <LaunchIconButton
              disabled={disabled}
              label={LAUNCH_FORM_COPY.addDocument}
              selected={isOpen}
              onClick={event =>
                addableFiles.length ? onClick(event) : showUpload({ templateDocumentId: null })
              }
            >
              <Plus size={ICON_SIZE} />
            </LaunchIconButton>
          )}
          uploadLabel={`${LAUNCH_FORM_COPY.orUpload}…`}
          onPick={documentId => onChange(addLaunchExtraDocument(choices, documentId))}
          onUpload={() => showUpload({ templateDocumentId: null })}
        />
      </div>
      {display.templateDocuments.map(document => (
        <div key={document.templateDocumentId} className={classes.rowBlock}>
          <TemplateDocumentRow
            choices={choices}
            disabled={disabled}
            document={document}
            replaceableFiles={replaceableFiles}
            onChange={onChange}
            onUpload={() => showUpload({ templateDocumentId: document.templateDocumentId })}
          />
          {uploadTarget?.templateDocumentId === document.templateDocumentId &&
            renderUploadSlot(uploadTarget)}
          <LaunchDocumentError
            message={errors[getLaunchDocumentBlockId(document.templateDocumentId)]}
          />
        </div>
      ))}
      {display.extraDocuments.map(file => (
        <DocumentRow
          key={file.documentId}
          caption={`${DOCUMENT_ROLE_LABELS[GQL.WorkflowFileDocumentRole.General]} · ${file.sourcePhrase}`}
          name={file.name}
        >
          <LaunchIconButton
            disabled={disabled}
            label={LAUNCH_FORM_COPY.remove}
            subject={file.name}
            onClick={() => onChange(removeLaunchExtraDocument(choices, file.documentId))}
          >
            <X size={ICON_SIZE} />
          </LaunchIconButton>
        </DocumentRow>
      ))}
      <LaunchDocumentError message={errors[LAUNCH_EXTRA_DOCUMENTS_BLOCK_ID]} />
      {uploadTarget?.templateDocumentId === null && renderUploadSlot(uploadTarget)}
      {spec.skippedFiles.map(file => (
        <Typography key={file.name} className={classes.caption}>
          {getSkippedFileLine(file)}
        </Typography>
      ))}
    </div>
  );
});

/** A template document is kept (replace or leave out), or changed (restore). */
function TemplateDocumentRow({
  choices,
  disabled,
  document,
  onChange,
  onUpload,
  replaceableFiles,
}: {
  choices: LaunchDocumentChoices;
  disabled: boolean;
  document: LaunchFormTemplateDocument;
  onChange: (choices: LaunchDocumentChoices) => void;
  onUpload: () => void;
  replaceableFiles: readonly ConversationFile[];
}) {
  const { classes, cx } = useStyles();
  const { name, templateDocumentId } = document;
  const isExcluded = choices.excludedDocumentIds.includes(templateDocumentId);
  const row = {
    caption: isExcluded
      ? LAUNCH_FORM_COPY.notIncluded
      : (getReplacementLine(document) ?? DOCUMENT_ROLE_LABELS[document.documentRole]),
    isExcluded,
    name,
  };

  if (isExcluded || document.replacement)
    return (
      <DocumentRow {...row}>
        <LaunchIconButton
          disabled={disabled}
          label={isExcluded ? LAUNCH_FORM_COPY.restore : LAUNCH_FORM_COPY.restoreOriginal}
          subject={name}
          onClick={() => onChange(restoreLaunchTemplateDocument(choices, templateDocumentId))}
        >
          <Undo2 size={ICON_SIZE} />
        </LaunchIconButton>
      </DocumentRow>
    );

  return (
    <DocumentRow {...row}>
      <FilePickerMenu
        files={replaceableFiles}
        renderTrigger={({ isOpen, onClick }) => (
          <AlineTooltip arrow placement="top" title={LAUNCH_FORM_COPY.replace}>
            <span>
              <Button
                aria-label={`${LAUNCH_FORM_COPY.replace}: ${name}`}
                className={cx(classes.replaceButton, isOpen && classes.selected)}
                disabled={disabled}
                endIcon={<ChevronDown size={ICON_SIZE} />}
                size="small"
                onClick={onClick}
              >
                {LAUNCH_FORM_COPY.replaceShort}
              </Button>
            </span>
          </AlineTooltip>
        )}
        uploadLabel={LAUNCH_FORM_COPY.uploadDifferent}
        onPick={documentId =>
          onChange(replaceLaunchTemplateDocument(choices, { documentId, templateDocumentId }))
        }
        onUpload={onUpload}
      />
      <LaunchIconButton
        disabled={disabled}
        label={LAUNCH_FORM_COPY.dontInclude}
        subject={name}
        onClick={() => onChange(excludeLaunchTemplateDocument(choices, templateDocumentId))}
      >
        <X size={ICON_SIZE} />
      </LaunchIconButton>
    </DocumentRow>
  );
}

function DocumentRow({
  caption,
  children,
  isExcluded = false,
  name,
}: {
  caption: string;
  children: React.ReactNode;
  name: string;
  isExcluded?: boolean;
}) {
  const { classes, cx } = useStyles();
  return (
    <div className={classes.row}>
      <div className={cx(classes.rowText, isExcluded && classes.excluded)}>
        <FileText className={classes.icon} size={ICON_SIZE} />
        <div className={classes.rowTextColumn}>
          <Typography className={classes.name}>{name}</Typography>
          <Typography className={classes.caption}>{caption}</Typography>
        </div>
      </div>
      <div className={classes.actions}>{children}</div>
    </div>
  );
}

/** Lists the conversation files with their source, then an upload entry. */
function FilePickerMenu({
  files,
  onPick,
  onUpload,
  renderTrigger,
  uploadLabel,
}: {
  files: readonly ConversationFile[];
  onPick: (documentId: string) => void;
  onUpload: () => void;
  renderTrigger: (props: MenuTriggerProps) => React.ReactNode;
  uploadLabel: string;
}) {
  const { classes } = useStyles();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const closeThen = (action: () => void) => () => {
    setAnchor(null);
    action();
  };
  return (
    <>
      {renderTrigger({
        isOpen: Boolean(anchor),
        onClick: event => setAnchor(event.currentTarget),
      })}
      <Menu
        anchorEl={anchor}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        open={Boolean(anchor)}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        onClose={() => setAnchor(null)}
      >
        {files.map(file => (
          <MenuItem key={file.documentId} onClick={closeThen(() => onPick(file.documentId))}>
            <ListItemIcon className={classes.menuIcon}>
              <FileText size={ICON_SIZE} />
            </ListItemIcon>
            <ListItemText
              primary={file.name}
              secondary={getConversationFileSourcePhrase(file)}
              slotProps={{ secondary: { className: classes.caption } }}
            />
          </MenuItem>
        ))}
        <MenuItem onClick={closeThen(onUpload)}>
          <ListItemIcon className={classes.menuIcon}>
            <Upload size={ICON_SIZE} />
          </ListItemIcon>
          <ListItemText primary={uploadLabel} />
        </MenuItem>
      </Menu>
    </>
  );
}

/** An icon button with an AlineTooltip; `subject` names the row for screen readers. */
function LaunchIconButton({
  children,
  disabled,
  label,
  onClick,
  selected = false,
  subject,
}: {
  children: React.ReactNode;
  disabled: boolean;
  label: string;
  onClick: (event: React.MouseEvent<HTMLElement>) => void;
  selected?: boolean;
  subject?: string;
}) {
  const { classes, cx } = useStyles();
  return (
    <AlineTooltip arrow placement="top" title={label}>
      <span>
        <IconButton
          aria-label={subject ? `${label}: ${subject}` : label}
          className={cx(classes.icon, selected && classes.selected)}
          disabled={disabled}
          size="small"
          onClick={onClick}
        >
          {children}
        </IconButton>
      </span>
    </AlineTooltip>
  );
}

function LaunchDocumentError({ message }: { message: string | undefined }) {
  const { classes } = useStyles();
  if (!message) return null;
  return (
    <Typography className={classes.error} role="alert">
      {message}
    </Typography>
  );
}
